import { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import useLocalStorageState from "use-local-storage-state";
import Tooltip from "@mui/material/Tooltip";
import {
  IconLayoutDashboard,
  // IconLayers,
  IconInbox,
  IconUser,
  IconId,
  IconBuilding,
  IconCash,
  IconClipboardList,
  IconShoppingCart,
  IconFileDescription,
  IconClipboardData,
  IconSitemap,
  IconSpeakerphone,
  IconTool,
  IconCalendar,
  IconChartPie,
  IconAdjustments,
  IconSettings,
  IconChevronLeft,
  IconChevronRight,
  IconLayoutSidebarLeftCollapse,
  IconMail,
  IconBrandWhatsapp,
  IconMessage,
  IconCloud,
  IconMapPin,
  IconCalculator,
} from "@tabler/icons-react";
import favicon from "../Utils/assets/img/pugCrm_favicon.png";

const tooltipSx = {
  tooltip: {
    sx: {
      backgroundColor: "#7a3a5e",
      color: "#ffffff",
      fontSize: "0.75rem",
      fontWeight: 500,
      borderRadius: "6px",
      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
    },
  },
  arrow: { sx: { color: "#7a3a5e" } },
};

export const Aside = ({
  collapsed = false,
  setCollapsed,
  onNavigate,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [PERMISSION] = useLocalStorageState("permission");
  const [expandedMenu, setExpandedMenu] = useState(null);

  const isQuotation = PERMISSION?.quotation?.isAuthorized;
  const isPurchaseOrder = PERMISSION?.purchase_order?.isAuthorized;
  const isReport = PERMISSION?.reports?.isAuthorized;
  const isCalendar = PERMISSION?.calendar?.isAuthorized;
  const isProject = PERMISSION?.project?.isAuthorized;
  const isSales = PERMISSION?.sales_order?.isAuthorized;
  const projectView = PERMISSION?.dashboard?.projectView;
  const inboxView = PERMISSION?.dashboard?.inboxView;

  const isActiveRoute = (path) => {
    if (!path || path === "#") return false;
    return (
      location.pathname === path ||
      location.pathname.startsWith(path + "/")
    );
  };

  const handlePlanGate = (allowed, message) => (e) => {
    if (!allowed) {
      e.preventDefault();
      toast.warning(message);
    } else {
      onNavigate?.();
    }
  };

  const navigationGroups = [
    {
      groupName: "Main",
      items: [
        {
          label: "Dashboards",
          icon: IconLayoutDashboard,
          isParent: true,
          key: "dashboards",
          children: [
            {
              path: "/dashboard",
              label: "Dashboard",
              icon: IconLayoutDashboard,
              canView: true,
            },
            {
              path: "/ProjectDashboard",
              label: "Projects View",
              icon: IconClipboardData,//iconLayers,
              canView: projectView,
            },
            {
              path: "/inbox",
              label: "Inbox",
              icon: IconInbox,
              canView: inboxView,
            },
          ],
        },
        { label: "Leads", path: "/leads", icon: IconUser },
        { label: "Contacts", path: "/contacts", icon: IconId },
        { label: "Company", path: "/company", icon: IconBuilding },
        { label: "Deals", path: "/deals", icon: IconCash },
      ],
    },
    {
      groupName: "Sales & Logistics",
      items: [
        {
          label: "Quotations",
          path: "/quotations",
          icon: IconClipboardList,
          handler: handlePlanGate(isQuotation, "Upgrade your plan to see quotations!"),
        },
        {
          label: "Purchase Orders",
          path: "/purchaseorder",
          icon: IconShoppingCart,
          handler: handlePlanGate(
            isPurchaseOrder,
            "Upgrade your plan to see Purchase Orders!"
          ),
        },
        {
          label: "Sales Orders",
          path: "/salesorder",
          icon: IconFileDescription,
          handler: handlePlanGate(isSales, "Upgrade your plan to see Sales Orders!"),
        },
        {
          label: "Projects",
          path: "/projects",
          icon: IconClipboardData,
          handler: handlePlanGate(isProject, "Upgrade your plan to see Projects!"),
        },
      ],
    },
    {
      groupName: "Apps & Marketing",
      items: [
        { label: "Workflows", path: "/workflow", icon: IconSitemap },
        {
          label: "Campaigns",
          icon: IconSpeakerphone,
          isParent: true,
          key: "campaigns",
          children: [
            { path: "/email", label: "Email Campaign", icon: IconMail },
            { path: "/whatsapp", label: "WhatsApp Campaign", icon: IconBrandWhatsapp },
            { path: "/sms", label: "SMS Campaign", icon: IconMessage },
          ],
        },
        {
          label: "Tools",
          icon: IconTool,
          isParent: true,
          key: "tools",
          children: [
            { path: "/documentManager", label: "My Documents", icon: IconCloud },
            { path: "/tracking", label: "Trip Tracking", icon: IconMapPin },
            {
              path: "/tools/gstCalculator",
              label: "GST Calculator",
              icon: IconCalculator,
            },
          ],
        },
        {
          label: "Calendar",
          path: "/calender",
          icon: IconCalendar,
          handler: handlePlanGate(isCalendar, "Upgrade your plan to see Calendar!"),
        },
        {
          label: "Reports",
          path: "/reports",
          icon: IconChartPie,
          handler: handlePlanGate(isReport, "Upgrade your plan to see Reports!"),
        },
      ],
    },
  ];

  const bottomNavItems = [
    {
      label: "CRM Setup",
      path: "/setup/companyDetails",
      icon: IconAdjustments,
    },
    { label: "Settings", path: "/settings/masters", icon: IconSettings },
  ];

  const renderLink = (item) => {
    const Icon = item.icon;
    const active = isActiveRoute(item.path);
    const link = (
      <Link
        to={item.path || "#"}
        onClick={(e) => {
          if (item.handler) item.handler(e);
          else onNavigate?.();
        }}
        className={`crm-aside__link${active ? " crm-aside__link--active" : ""}`}
        title={collapsed ? item.label : undefined}
      >
        <Icon size={20} stroke={active ? 2 : 1.75} className="crm-aside__icon" />
        {!collapsed && <span className="crm-aside__label">{item.label}</span>}
      </Link>
    );

    if (!collapsed) return <li key={item.path}>{link}</li>;

    return (
      <li key={item.path}>
        <Tooltip title={item.label} placement="right" arrow componentsProps={tooltipSx}>
          <span>{link}</span>
        </Tooltip>
      </li>
    );
  };

  const renderParent = (item) => {
    const Icon = item.icon;
    const isExpanded = expandedMenu === item.key;
    const isAnyChildActive = item.children.some((c) => isActiveRoute(c.path));

    const collapsedTooltip = (
      <div>
        {item.children.map((child, idx) => (
          <div
            key={child.path}
            className="news-item"
            onClick={() => {
              if (child.canView === false) {
                toast.warning("You are not authorized for this action!");
                return;
              }
              navigate(child.path);
              onNavigate?.();
            }}
            style={{
              cursor: "pointer",
              padding: "6px 8px",
              borderRadius: 5,
              background: isActiveRoute(child.path) ? "#fff" : "transparent",
              color: isActiveRoute(child.path) ? "#000" : "#fff",
              marginBottom: idx < item.children.length - 1 ? 4 : 0,
            }}
          >
            {child.label}
          </div>
        ))}
      </div>
    );

    const button = (
      <button
        type="button"
        onClick={() => {
          if (!collapsed) {
            setExpandedMenu(isExpanded ? null : item.key);
          }
        }}
        className={`crm-aside__parent-btn${isAnyChildActive ? " crm-aside__parent-btn--active" : ""
          }`}
      >
        <Icon
          size={20}
          stroke={isAnyChildActive ? 2 : 1.75}
          className="crm-aside__icon"
        />
        {!collapsed && (
          <>
            <span className="crm-aside__label">{item.label}</span>
            <IconChevronRight
              size={14}
              className={`crm-aside__chevron${isExpanded ? " crm-aside__chevron--open" : ""
                }`}
            />
          </>
        )}
      </button>
    );

    return (
      <li key={item.key}>
        {collapsed ? (
          <Tooltip
            title={collapsedTooltip}
            placement="right-start"
            arrow
            componentsProps={tooltipSx}
          >
            <span>{button}</span>
          </Tooltip>
        ) : (
          button
        )}

        {!collapsed && isExpanded && (
          <ul className="crm-aside__children">
            {item.children.map((child) => {
              const ChildIcon = child.icon;
              const childActive = isActiveRoute(child.path);
              return (
                <li key={child.path}>
                  <Link
                    to={child.path}
                    onClick={(e) => {
                      if (child.canView === false) {
                        e.preventDefault();
                        toast.warning("You are not authorized for this action!");
                        return;
                      }
                      onNavigate?.();
                    }}
                    className={`crm-aside__child-link${childActive ? " crm-aside__child-link--active" : ""
                      }`}
                  >
                    <ChildIcon size={16} stroke={1.75} />
                    <span>{child.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </li>
    );
  };

  return (
    <aside
      id="sidebar"
      className={`crm-aside${collapsed ? " crm-aside--collapsed" : ""}`}
    >
      <div className="crm-aside__brand">
        <NavLink to="/dashboard" className="crm-aside__brand-link" onClick={onNavigate}>
          {collapsed ? (
            <div className="crm-aside__logo-mark">
              <img src={favicon} alt="PugCRM" />
            </div>
          ) : (
            <>
              <div className="crm-aside__logo-mark">
                <img src={favicon} alt="PugCRM" />
              </div>
              <div className="crm-aside__brand-text">
                <span className="crm-aside__brand-title">PugCRM</span>
                <span className="crm-aside__brand-sub">Workspace</span>
              </div>
            </>
          )}
        </NavLink>

        {!collapsed && setCollapsed && (
          <button
            type="button"
            className="crm-aside__collapse-btn"
            onClick={() => setCollapsed(true)}
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
          >
            <IconChevronLeft size={20} />
          </button>
        )}
      </div>

      <nav className="crm-aside__nav crm-shell-scroll">
        {navigationGroups.map((group) => (
          <div key={group.groupName}>
            {!collapsed ? (
              <p className="crm-aside__group-label">{group.groupName}</p>
            ) : (
              <div className="crm-aside__group-divider" />
            )}
            <ul className="crm-aside__list">
              {group.items.map((item) =>
                item.isParent ? renderParent(item) : renderLink(item)
              )}
            </ul>
          </div>
        ))}
      </nav>

      <div className="crm-aside__bottom">
        <ul className="crm-aside__list">
          {bottomNavItems.map((item) => renderLink(item))}
        </ul>
      </div>

      {setCollapsed && (
        <div className="crm-aside__footer">
          <button
            type="button"
            className="crm-aside__footer-btn"
            onClick={() => setCollapsed(!collapsed)}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? (
              <IconChevronRight size={17} />
            ) : (
              <>
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <IconLayoutSidebarLeftCollapse size={16} />
                  <span>Collapse sidebar</span>
                </span>
                <kbd className="crm-aside__kbd">ESC</kbd>
              </>
            )}
          </button>
        </div>
      )}
    </aside>
  );
};
